import React, { useState } from 'react';
import { Box, Grid, Typography, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import PageLayout from '../components/PageLayout';
import { products, categories } from '../data/products';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '3.5rem',
  color: '#1a1a1a',
  textAlign: 'center',
  marginBottom: '3rem',
  '@media (max-width: 600px)': {
    fontSize: '2.5rem',
  }
});

const CollectionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Update gender when location changes
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gender = params.get('gender') || 'all';
    setSelectedGender(gender);
  }, [location]);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedGender, setSelectedGender] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('gender') || 'all';
  });

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesGender = selectedGender === 'all' || product.gender === selectedGender;
    return matchesCategory && matchesGender;
  });

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const handleGenderChange = (event, newValue) => {
    setSelectedGender(newValue);
    navigate(newValue === 'all' ? '/collections' : `/collections?gender=${newValue}`, { replace: true });
  };

  const getBreadcrumbText = () => {
    if (selectedGender === 'all') return 'All Collections';
    return `${selectedGender.charAt(0).toUpperCase() + selectedGender.slice(1)}'s Collection`;
  };

  return (
    <PageLayout>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <PageTitle>
            {getBreadcrumbText()}
          </PageTitle>
        </motion.div>

        <Box sx={{ mb: 2 }}>
          <Tabs 
            value={selectedGender}
            onChange={handleGenderChange}
            centered
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                color: '#666',
                fontSize: '1rem',
                textTransform: 'none',
                minWidth: 'auto',
                padding: '12px 24px',
                '&.Mui-selected': {
                  color: '#c4a661',
                  fontWeight: 'bold',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#c4a661',
                height: '3px',
              },
            }}
          >
            <Tab label="All" value="all" />
            <Tab label="Women" value="women" />
            <Tab label="Men" value="men" />
            <Tab label="Unisex" value="unisex" />
          </Tabs>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Tabs 
            value={selectedCategory}
            onChange={handleCategoryChange}
            centered
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                color: '#666',
                fontSize: '1rem',
                textTransform: 'none',
                minWidth: 'auto',
                padding: '12px 24px',
                '&.Mui-selected': {
                  color: '#c4a661',
                  fontWeight: 'bold',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#c4a661',
                height: '3px',
              },
              '& .MuiTabs-scrollButtons': {
                color: '#666',
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            <Tab label="All" value="all" />
            {Object.entries(categories).map(([id, category]) => (
              <Tab key={id} label={category.name} value={id} />
            ))}
          </Tabs>
        </Box>

        {filteredProducts.length > 0 ? (
          <Grid container spacing={4}>
            {filteredProducts.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <ProductCard product={item} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="textSecondary" gutterBottom>
              No products found in this category
            </Typography>
            <Typography color="textSecondary">
              Please try selecting a different category
            </Typography>
          </Box>
        )}
    </PageLayout>
  );
};

export default CollectionsPage;
